import {
    MarketplaceTax,
    OrganizationMarketplace,
    OrganizationMarketplaceServicesInfo,
    OrganizationMarketplaceTax
} from '@spike/marketplace-model';
import { Element } from 'components/Service/ChipSelector';
import Service from 'model/Service';

export interface MarketplaceTaxGroup {
    marketplaceId: number;
    marketplaceName: string;
    taxes: Array<OrganizationMarketplaceTax>;
    totalAvailable: number;
}

export const taxToElement = (tax: OrganizationMarketplaceTax): Element<number> => ({
    id: tax.id,
    name: `${tax.name} (${tax.value}%)`
});

export const buildTaxElements = (taxes: Array<OrganizationMarketplaceTax>): Array<Element<number>> =>
    taxes.map(taxToElement);

export const buildSelectedTaxElements = (
    taxes: Array<OrganizationMarketplaceTax>,
    selectedTaxIds: Array<number>
): Array<Element<number>> => taxes.filter(tax => selectedTaxIds.includes(tax.id)).map(taxToElement);

const toMarketplaceTax = (tax: OrganizationMarketplaceTax): MarketplaceTax => ({
    id: tax.id,
    uuid: String(tax.id),
    name: tax.name,
    percentage: tax.value
});

export const buildPricingTaxesFromMarketplaces = (
    marketplaces: Service['marketplaces'],
    organizationServicesInfo: Array<OrganizationMarketplaceServicesInfo>
): Array<MarketplaceTax> => {
    const selectedTaxes: Array<MarketplaceTax> = [];
    const seenTaxIds = new Set<number>();

    marketplaces.forEach(marketplace => {
        const servicesInfo = organizationServicesInfo.find(info => info.marketplace.id === marketplace.marketplaceId);

        if (!servicesInfo) {
            return;
        }

        marketplace.taxes.forEach(taxId => {
            if (seenTaxIds.has(taxId)) {
                return;
            }

            const tax = servicesInfo.taxes.find(servicesInfoTax => servicesInfoTax.id === taxId);

            if (tax) {
                seenTaxIds.add(taxId);
                selectedTaxes.push(toMarketplaceTax(tax));
            }
        });
    });

    return selectedTaxes;
};

export const buildMarketplaceTaxGroups = (
    marketplaces: Service['marketplaces'],
    organizationServicesInfo: Array<OrganizationMarketplaceServicesInfo>,
    organizationMarketplaces: Array<OrganizationMarketplace> = []
): Array<MarketplaceTaxGroup> =>
    marketplaces
        .map(marketplace => {
            const servicesInfo = organizationServicesInfo.find(
                info => info.marketplace.id === marketplace.marketplaceId
            );
            const organizationMarketplace = organizationMarketplaces.find(om => om.id === marketplace.marketplaceId);

            const marketplaceName = servicesInfo?.marketplace.name ?? organizationMarketplace?.name ?? '';

            const taxes = marketplace.taxes
                .map(taxId => servicesInfo?.taxes.find(tax => tax.id === taxId))
                .filter((tax): tax is OrganizationMarketplaceTax => tax !== undefined);

            return {
                marketplaceId: marketplace.marketplaceId,
                marketplaceName,
                taxes,
                totalAvailable: servicesInfo?.taxes.length ?? 0
            };
        })
        .filter(group => group.marketplaceName.length > 0)
        .sort((left, right) =>
            left.marketplaceName.toLocaleLowerCase().localeCompare(right.marketplaceName.toLocaleLowerCase())
        );
